import { gql } from '@apollo/client'
import type { Chat, RouteDef } from '@ztechapps/common.contract'
import { useActionMutation, useError, useToast } from '@ztechapps/common.hook'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'

const createOrderFromConversationMutation = gql`
  mutation createOrderFromConversation($input: CreateOrderFromConversationArgs!) {
    createOrderFromConversation(input: $input) {
      order {
        _id
      }
      credentialsMailSent
    }
  }
`

type CreateOrderFromConversationResult = {
  order: { _id: string }
  // true: the sign-in mail was sent · false: a send was attempted and failed · null/absent: none was needed.
  credentialsMailSent?: boolean | null
}

type CreateOrderFromConversationInput = {
  chatId: string
}

export type UseCreateOrderFromConversationProps = {
  /** Receives the new order's detail route; the host navigates to it. */
  onSuccessSubmit?: (route: string) => void
}

export type UseCreateOrderFromConversationResult = {
  /** Resolves false when the order was not created, so a list caller can recover — the same Promise<boolean>
   * success channel as libs/webui/location/src/AddressEditPopOver/useAddressEditPopOver.ts:22-24. */
  createOrderAndOpen: (chat: Chat) => Promise<boolean>
  loading: boolean
}

/** Creates a sales order from a conversation, warns when the new customer's sign-in mail failed, and hands the order's route on. */
export const useCreateOrderFromConversation = ({
  onSuccessSubmit,
}: UseCreateOrderFromConversationProps = {}): UseCreateOrderFromConversationResult => {
  const [createOrder, { error, loading }] = useActionMutation<
    CreateOrderFromConversationResult,
    CreateOrderFromConversationInput
  >({ query: createOrderFromConversationMutation })
  const { showWarning } = useToast()
  const orderItemRoute = useInject<RouteDef>(WebuiIocKey.routes.orderItem)
  // The refusal is toasted here, so the catch below only reports the failure to the caller.
  useError(error)

  const createOrderAndOpen = async (chat: Chat): Promise<boolean> => {
    try {
      const result = await createOrder({ chatId: chat._id })
      if (result?.credentialsMailSent === false) {
        showWarning({ message: 'Order created, but the sign-in mail could not be sent.' })
      }
      onSuccessSubmit?.(orderItemRoute.altPath({ _id: result?.order?._id }))
      return true
    } catch {
      return false
    }
  }

  return { createOrderAndOpen, loading }
}
